import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import WatchOutlined from '@mui/icons-material/WatchOutlined';
import { API_CONFIG } from '../../config/api';
import FitbitCollectionProfileCompletion from './FitbitCollectionProfileCompletion';
import { FitbitCollectionProfile } from '../../services/fitbitCollectionEnrollmentService';

const api = (path: string) => `${API_CONFIG.BASE_URL.replace(/\/$/, '')}/api/ultrahuman/${path}`;
const selector = (name: string) => {
  const values = new URLSearchParams(window.location.hash.slice(1)).getAll(name);
  return values.length === 1 && /^[A-Za-z0-9_-]{43}$/.test(values[0]) ? values[0] : null;
};

export function UltrahumanInvitationStart() {
  const [invitation] = useState(() => selector('invitation'));
  const submitted = useRef(false);
  const [opening, setOpening] = useState(false);
  useEffect(() => { if (window.location.hash) window.history.replaceState(window.history.state, '', window.location.pathname); }, []);
  return <Container maxWidth="sm"><Paper sx={{ my: 5, p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
    <WatchOutlined sx={{ color: 'primary.main', fontSize: 48, mb: 2 }} />
    <Typography variant="h4" component="h1" gutterBottom>Connect Ultrahuman to your study</Typography>
    {invitation ? <>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Choose which Ultrahuman data you share with the research team. After connecting, complete your study profile.</Typography>
      <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>StudySync collects your available history and keeps it up to date. Your researcher can view and export your shared Ring and glucose measurements.</Alert>
      <Box component="form" method="post" action={api('authorization/start')} onSubmit={event => {
        if (submitted.current) { event.preventDefault(); return; }
        submitted.current = true; setOpening(true);
      }}>
        <input type="hidden" name="invitationToken" value={invitation} />
        <Button type="submit" variant="contained" size="large" disabled={opening}>{opening ? 'Opening Ultrahuman…' : 'Continue to Ultrahuman'}</Button>
      </Box>
    </> : <Alert severity="info" sx={{ textAlign: 'left' }}>Open the personal link from your study invitation. If authorization did not finish, reopen that link and try again.</Alert>}
  </Paper></Container>;
}

type Profile = { firstName: string; lastName: string; age: number | string; gender: string;
  timezone: string | null; historyStart: string | null; completed: boolean; collectionReady?: boolean };
export function UltrahumanProfileCompletion() {
  const [session] = useState(() => selector('profileSession'));
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState('');
  const [revision, retry] = useState(0);
  useEffect(() => {
    let current = true;
    if (!session) { setError('Reopen your personal study invitation.'); return; }
    fetch(api(`enrollment/profile?profileSession=${session}`), { credentials: 'include' }).then(async response => {
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load your profile.');
      if (!current) return;
      setProfile(data);
    }).catch(reason => { if (current) setError(reason.message); });
    return () => { current = false; };
  }, [session, revision]);
  const save = async (values: FitbitCollectionProfile) => {
    setError('');
    const response = await fetch(api('enrollment/profile'), { method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ profileSession: session, profile: values }) });
    const data = await response.json();
    if (!response.ok) { setError(data.error || 'Could not complete enrollment.'); throw new Error('Enrollment failed'); }
    return data;
  };
  if (!profile) return <Container maxWidth="sm" sx={{ py: 5 }}>{error ? <Alert severity="error">{error}</Alert> : <CircularProgress aria-label="Loading your profile" />}</Container>;
  if (profile.completed) return <Container maxWidth="sm" sx={{ py: 5 }}><Alert severity="success">Your Ultrahuman enrollment is complete.</Alert></Container>;
  if (profile.collectionReady === false) return <Container maxWidth="sm" sx={{ py: 5 }}>
    <Alert severity="warning" action={<Button onClick={() => { setProfile(null); setError(''); retry(value => value + 1); }}>Retry</Button>}>
      We couldn't read your Ultrahuman account settings. Please try again.
    </Alert>
  </Container>;
  return <FitbitCollectionProfileCompletion sourceLabel="Ultrahuman" completeProfile={save}
    initialProfile={{ firstName: profile.firstName, lastName: profile.lastName, age: String(profile.age), gender: profile.gender }}
    extraFields={error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : undefined} />;
}
